import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import {
  useUserControllerServiceGetAssignedLocations,
  useUserControllerServiceSwitchLastLoggedInLocation,
} from "../../../services/queries";
import {
  showSnackbar,
  triggerRefetchProfile,
} from "../../../redux/slices/auth.slice";
import { LocationOption, LocationPopoverProps } from "./types";
import { Loader } from "../../../common/components/loader";
import { useInfiniteScroll } from "../../../common/hooks/useInfiniteScroll";

const LOCATIONS_PAGE_SIZE = 20;

const LOCATIONS_SEARCH_PAGE_SIZE = 500;

export const LocationPopover: React.FC<LocationPopoverProps> = ({
  anchorEl,
  onClose,
  currentLocation,
  onLocationSelect,
}) => {
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const isSearching = debouncedSearch.trim().length > 0;

  // Reset the term whenever the popover is reopened, so it never opens
  // showing a filtered list from last time.
  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const {
    options: locations,
    handleScroll,
    isFetching,
  } = useInfiniteScroll(
    (page: number) =>
      useUserControllerServiceGetAssignedLocations(
        {
          page,
          pageSize: isSearching
            ? LOCATIONS_SEARCH_PAGE_SIZE
            : LOCATIONS_PAGE_SIZE,
          sortBy: "name",
          sortDirection: "ASC",
        },

        undefined,

        { enabled: open },
      ),

    [open, isSearching],
    0,
    "locationUuid",
  );

  const visibleLocations = useMemo(() => {
    if (!isSearching) return locations;
    const query = debouncedSearch.trim().toLowerCase();
    return locations.filter((location: LocationOption) =>
      [
        location?.locationName,
        location?.address?.city,
        location?.address?.state,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [locations, debouncedSearch, isSearching]);

  const isLoadingFirstPage = isFetching && locations.length === 0;

  const handleListScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const list = event.currentTarget;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10) {
      handleScroll?.();
    }
  };

  const { mutateAsync: switchLocation, isPending: isSwitchingLocation } =
    useUserControllerServiceSwitchLastLoggedInLocation({
      onSuccess: (response: any) => {
        dispatch(
          showSnackbar({
            message: response?.message || "Location switched successfully",
            severity: "success",
          }),
        );
        dispatch(triggerRefetchProfile());
      },
      onError: (error: any) => {
        dispatch(
          showSnackbar({
            message: error?.body?.message || "Failed to switch location",
            severity: "error",
          }),
        );
      },
    });

  const handleLocationSelect = async (location: LocationOption) => {
    try {
      await switchLocation({ locationUuid: location?.locationUuid });
      onLocationSelect(location);
      onClose();
    } catch (error) {
      console.error("Failed to switch location:", error);
    }
  };

  const formatLocationAddress = (addressDto: LocationOption["address"]) => {
    const parts = [addressDto?.city, addressDto?.state]?.filter(Boolean);
    return parts?.join(", ");
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 300,
            ml: -1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            border: "1px solid #E0E0E0",
          },
        },
      }}
    >
      <Box>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: "#1A1A1A",
            }}
          >
            Switch Location
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search location"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#666666" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 14,
              },
            }}
          />
        </Box>

        <Divider />

        {isLoadingFirstPage ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#666666" }}>
              Loading locations...
            </Typography>
          </Box>
        ) : (
          <List
            sx={{ py: 0, maxHeight: 300, overflow: "auto" }}
            onScroll={handleListScroll}
          >
            {visibleLocations?.map((location) => {
              const locationAddress = formatLocationAddress(location?.address);
              const isSelected =
                location?.lastLoggedIn ||
                (!visibleLocations?.some((loc) => loc?.lastLoggedIn) &&
                  (currentLocation?.includes(location?.locationName) ||
                    currentLocation?.includes(locationAddress)));

              return (
                <ListItem
                  key={location?.locationUuid}
                  onClick={() => handleLocationSelect(location)}
                  sx={{
                    cursor: "pointer",
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                    backgroundColor: isSelected ? "#E3F2FD" : "transparent",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <FmdGoodOutlinedIcon
                      sx={{
                        color: isSelected ? "#1976D2" : "#666666",
                        fontSize: 20,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isSelected ? 600 : 400,
                            fontSize: 14,
                            color: isSelected ? "#1976D2" : "#1A1A1A",
                          }}
                        >
                          {location?.locationName}
                        </Typography>
                        {location?.lastLoggedIn && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 11,
                              color: "#1976D2",
                              backgroundColor: "#E3F2FD",
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 500,
                            }}
                          >
                            Current
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 13,
                          color: "#666666",
                        }}
                      >
                        {locationAddress}
                      </Typography>
                    }
                  />
                  {isSelected && (
                    <CheckIcon
                      sx={{
                        color: "#1976D2",
                        fontSize: 20,
                        ml: 1,
                      }}
                    />
                  )}
                </ListItem>
              );
            })}

            {!isSearching && isFetching && locations.length > 0 && (
              <Box sx={{ p: 1.5, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Loading more...
                </Typography>
              </Box>
            )}
            {visibleLocations.length === 0 && !isFetching && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  {isSearching
                    ? "No locations match your search"
                    : "No locations available"}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
      <Loader showLoader={isSwitchingLocation} />
    </Popover>
  );
};
